import { SupplierInput } from "@/app/Suppliers/Components/SupplierInput";
import { VehicleInput } from "@/app/Vehicles/Components/VehicleInput";
import {
  Currency,
  DepositRefundedAt,
  FeatureFlagType,
  HireUnitType,
  HoldingDepositRefundedBy,
  QuerySupplierOfficesOrderByColumn,
  RelocationFieldsFragment,
  SortOrder,
} from "@/gql/graphql";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { RelocationReferencesInput } from "@/app/Relocations/Components/RelocationReferencesInput";
import { HireUnitTypeInput } from "@/app/Common/Components/HireUnitType";
import { DepositRefundedAtInput } from "@/app/Common/Components/DepositRefundedAtInput";
import { Suspense, useEffect, useState } from "react";
import { RelocationFormValues } from "@/app/Relocations/Utils/getDefaultRelocationValues";
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";
import { CurrencyTypeInput } from "@/app/Common/Components/CurrencyTypeInput";
import { MeasurementInput } from "@/app/Common/Components/MeasurementInput";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { HoldingDepositRefundedByInput } from "@/app/Common/Components/HoldingDepositRefundedByInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { SupplierInsuranceOptionInput } from "@/app/Suppliers/Components/SupplierInsuranceOptionInput";
import { useGqlQuery, useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { RelocationTripCard } from "@/app/Relocations/Forms/RelocationTripCard";
import { SupplierOfficeInput } from "@/app/Offices/Components/SupplierOfficeInput";
import { NumberInput } from "@/lib/Components/Form/Inputs/NumberInput";
import { StepperInput } from "@/lib/Components/Form/Inputs/StepperInput";
import { QuantityBar } from "@/app/Relocations/Components/QuantityBar";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { DistanceAllowedInput } from "@/lib/Components/Form/Inputs/DistanceAllowedInput";
import { supplierTripByOfficesQuery } from "@/app/SupplierTrips/GraphQL/supplierTripRecord";
import { tripByCities } from "@/app/Cities/Trips/GraphQL/tripByCities";
import { useFeatureFlag } from "@/app/Meta/utils/useFeatureFlag";
import { RelocationInclusionForm } from "@/app/Relocations/Forms/RelocationInclusionForm";
import { AdminSection } from "@/lib/Components/Common/AdminSection";
import { DollarSignIcon, TriangleAlertIcon } from "lucide-react";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { MultiSelectInput } from "@/lib/Components/Form/Inputs/MultiSelectInput";
import { useFormikContext } from "formik";
import { FormErrorSummary } from "@/app/Relocations/Components/FormErrorSummary";
import { DepartureDatesWarning } from "@/app/Relocations/Components/DepartureDatesWarning";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/catalyst/button";
import { Link } from "@/components/catalyst/link";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { clsx } from "clsx";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  hasRouteAndVehicle,
  useHireUnitLabels,
  useSupplierFormDefaults,
} from "@/app/Relocations/Forms/relocationFormHooks";

function DateInPastWarning({ message }: { message: string }) {
  return (
    <Tooltip>
      <TooltipTrigger
        type="button"
        className="absolute bottom-[10px] right-3 text-amber-500 hover:text-amber-600"
      >
        <TriangleAlertIcon className="h-4 w-4" />
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  );
}

export function SupplierFieldsSkeleton() {
  return (
    <div className="col-span-full">
      <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
        {/* Row 1: From + To */}
        <div className="col-span-3 space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10" />
        </div>
        <div className="col-span-3 space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10" />
        </div>
        {/* Row 2: Vehicle + Quantity + License Plates */}
        <div className="col-span-2 space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-10" />
        </div>
        <div className="col-span-1 space-y-2">
          <Skeleton className="h-4 w-14" />
          <Skeleton className="h-10" />
        </div>
        <div className="col-span-3 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10" />
        </div>
      </div>
    </div>
  );
}

export function RelocationForm({
  initialShowAdvanced = false,
  relocation,
  showAdditionalSettings = !relocation,
}: {
  relocation?: RelocationFieldsFragment;
  initialShowAdvanced?: boolean;
  showAdditionalSettings?: boolean;
}) {
  const { values, initialValues } = useFormikContext<RelocationFormValues>();
  const t = useTranslations("relocation.form");

  return (
    <>
      {!initialValues.supplier?.connect ? (
        <div className="col-span-full mb-6 border-l-4 border-blue-500 pl-4">
          <SupplierInput name="supplier.connect" label={t("supplier")} />
        </div>
      ) : null}

      {values.supplier.connect ? (
        <Suspense fallback={<SupplierFieldsSkeleton />}>
          <FieldsWithSupplier
            initialShowAdvanced={initialShowAdvanced}
            relocation={relocation}
            showAdditionalSettings={showAdditionalSettings}
          />
        </Suspense>
      ) : null}
    </>
  );
}

function HireUnitsWarningContent({
  cityAId,
  cityBId,
  hireUnitsAllowed,
  hireUnitType,
}: {
  cityAId: string;
  cityBId: string;
  hireUnitsAllowed: number | null | undefined;
  hireUnitType: HireUnitType | null | undefined;
}) {
  const t = useTranslations("relocation.form.hireUnitsWarning");
  const { data } = useSuspenseGqlQuery(tripByCities, {
    cityAId,
    cityBId,
  });

  const durationHrs = data.tripByCities.duration_hrs;

  if (!durationHrs || !hireUnitsAllowed || hireUnitsAllowed <= 0) {
    return null;
  }

  // For Night/24hrs: 1 unit = 2 calendar days of driving (pickup day + delivery day)
  // For Day: units = calendar days
  const drivingDays =
    hireUnitType === HireUnitType.Day ? hireUnitsAllowed : hireUnitsAllowed + 1;

  const drivingHoursPerDay = durationHrs / drivingDays;

  if (drivingHoursPerDay <= 4) {
    return null;
  }

  // Calculate suggested units based on hire unit type
  const suggestedDrivingDays = Math.max(2, Math.ceil(durationHrs / 4));
  const suggestedUnits =
    hireUnitType === HireUnitType.Day
      ? suggestedDrivingDays
      : Math.max(1, suggestedDrivingDays - 1);

  return (
    <div className="col-span-full rounded-md bg-yellow-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <TriangleAlertIcon className="h-5 w-5 text-yellow-400" />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">{t("title")}</h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>{t("description", { hours: drivingHoursPerDay.toFixed(1) })}</p>
            <p className="mt-2">
              {t.rich("suggestion", {
                b: (chunks) => <strong>{chunks}</strong>,
              })}{" "}
              <strong>{suggestedUnits}</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function useHireUnitsWarning() {
  const { values } = useFormikContext<RelocationFormValues>();

  const canQuery =
    !!values.departureOffice?.connect && !!values.deliveryOfficeIds?.[0];

  const { data: supplierTripData } = useGqlQuery(
    supplierTripByOfficesQuery,
    {
      officeAId: values.departureOffice?.connect ?? "",
      officeBId: values.deliveryOfficeIds?.[0] ?? "",
    },
    { enabled: canQuery },
  );

  const cityAId = supplierTripData?.supplierTripByOffices?.officeA?.city?.id;
  const cityBId = supplierTripData?.supplierTripByOffices?.officeB?.city?.id;

  const { data: tripData } = useGqlQuery(
    tripByCities,
    {
      cityAId: cityAId ?? "",
      cityBId: cityBId ?? "",
    },
    { enabled: !!cityAId && !!cityBId },
  );

  const durationHrs = tripData?.tripByCities?.duration_hrs;
  const hireUnitsAllowed = values.hire_units_allowed;
  const hireUnitType = values.hire_unit_type;

  if (!durationHrs || !hireUnitsAllowed || hireUnitsAllowed <= 0) {
    return { showWarning: false, cityAId, cityBId };
  }

  const drivingDays =
    hireUnitType === HireUnitType.Day ? hireUnitsAllowed : hireUnitsAllowed + 1;

  const drivingHoursPerDay = durationHrs / drivingDays;

  return {
    showWarning: drivingHoursPerDay > 4,
    cityAId,
    cityBId,
  };
}

function HireUnitsWarning() {
  const { values } = useFormikContext<RelocationFormValues>();
  const { showWarning, cityAId, cityBId } = useHireUnitsWarning();

  if (!showWarning || !cityAId || !cityBId) {
    return null;
  }

  return (
    <HireUnitsWarningContent
      cityAId={cityAId}
      cityBId={cityBId}
      hireUnitsAllowed={values.hire_units_allowed}
      hireUnitType={values.hire_unit_type}
    />
  );
}

function HireUnitsInput({
  unitLabel,
  currency,
  hireUnitRate,
  className = "col-span-2",
}: {
  unitLabel: string;
  currency: Currency;
  hireUnitRate: number;
  className?: string;
}) {
  const t = useTranslations("relocation.form");
  const { showWarning } = useHireUnitsWarning();
  const { values, setFieldValue } = useFormikContext<RelocationFormValues>();

  return (
    <NumberInput
      name="hire_units_allowed"
      className={className}
      onChange={async (hireUnitsAllowed) => {
        if (
          hireUnitsAllowed !== null &&
          values.minimum_hire_units > hireUnitsAllowed
        ) {
          await setFieldValue("minimum_hire_units", hireUnitsAllowed);
        }
      }}
      inputProps={{
        id: "hire_units_allowed",
        min: 0,
        className: showWarning
          ? "ring-2 ring-yellow-400 rounded-lg"
          : undefined,
      }}
      label={t("relocationHire")}
      suffix={`${unitLabel}s`}
      tooltip={t("relocationHireTooltip", {
        unit: unitLabel,
        rate: formatCurrency(hireUnitRate, currency),
      })}
    />
  );
}

function ConfigurationSection({ values }: { values: RelocationFormValues }) {
  const tForm = useTranslations("relocation.form");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-lg bg-orange-100 p-2">
          <Cog6ToothIcon className="h-5 w-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {tForm("configurationTitle")}
          </h2>
          <p className="text-sm text-gray-500">
            {tForm("configurationDescription")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-4">
        <CurrencyTypeInput
          name="currency"
          label={tForm("currency")}
          className="col-span-3"
        />
        <MeasurementInput
          name="measurement"
          label={tForm("measurement")}
          className="col-span-3"
        />
        <HireUnitTypeInput
          name="hire_unit_type"
          label={tForm("hireUnit")}
          className="col-span-3"
        />
        <DateInput
          name="fixed_latest_departure_date"
          label={tForm("latestDeparture")}
          optionalLabel
          min={values.available_from_date}
          max={values.available_to_date}
          className="col-span-3"
          clearable
        />
        <SwitchInput
          name="is_ferry_required"
          label={tForm("requireFerryDate")}
          tooltip={tForm("requireFerryDateTooltip")}
          className="col-span-3"
        />
      </div>
    </div>
  );
}

function PricingSection({
  values,
  setFieldValue,
  currency,
  isAdmin,
  variableRelocationRateEnabled,
  capitalizedUnitLabel,
}: {
  values: RelocationFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  currency: Currency;
  isAdmin: boolean;
  variableRelocationRateEnabled: boolean;
  capitalizedUnitLabel: string;
}) {
  const tForm = useTranslations("relocation.form");

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="rounded-lg bg-amber-100 p-2">
          <DollarSignIcon className="size-5 text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {tForm("pricingTitle")}
          </h2>
          <p className="text-sm text-gray-500">{tForm("pricingDescription")}</p>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-4">
        <CurrencyInput
          name="hire_unit_rate"
          label={tForm("relocationRate")}
          tooltip={tForm("relocationRateTooltip")}
          currency={currency}
          isEditable={isAdmin || variableRelocationRateEnabled}
          className="col-span-3"
        />
        <CurrencyInput
          currency={currency}
          isEditable={false}
          name="extra_hire_unit_rate"
          label={tForm("extraUnitRate", { unit: capitalizedUnitLabel })}
          className="col-span-3"
        />
        <CurrencyInput
          currency={currency}
          onChange={async (value) => {
            if (value === null) return;
            await setFieldValue(
              "extra_hire_unit_rate",
              value + values.extra_hire_unit_deposit_rate,
            );
          }}
          name="extra_hire_unit_supplier_net_rate"
          label={tForm("supplierNet")}
          className="col-span-3"
        />
        <CurrencyInput
          currency={currency}
          isEditable={isAdmin}
          name="extra_hire_unit_deposit_rate"
          onChange={(value) => {
            setFieldValue(
              "extra_hire_unit_rate",
              (value ?? 0) + (values.extra_hire_unit_supplier_net_rate ?? 0),
            );
          }}
          label={tForm("imoovaNet")}
          className="col-span-3"
        />
      </div>
    </div>
  );
}

function FeesSection({
  values,
  setFieldValue,
  currency,
}: {
  values: RelocationFormValues;
  setFieldValue: (field: string, value: unknown) => void;
  currency: Currency;
}) {
  const tForm = useTranslations("relocation.form");

  return (
    <AdminSection className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="grid grid-cols-6 gap-4">
        <CurrencyInput
          currency={currency}
          name="booking_fee_amount"
          label={tForm("bookingFee")}
          className="col-span-2"
        />
        <CurrencyInput
          label={tForm("chargePerMove")}
          name="charge_per_move_amount"
          currency={currency}
          className="col-span-2"
        />
        <CurrencyInput
          currency={currency}
          name="holding_deposit_amount"
          label={tForm("holdingDeposit")}
          className="col-span-2"
        />
        {values.holding_deposit_amount > 0 ? (
          <>
            <HoldingDepositRefundedByInput
              name="holding_deposit_refunded_by"
              label={tForm("refundedBy")}
              onChange={(value) => {
                if (value === HoldingDepositRefundedBy.Imoova) {
                  setFieldValue(
                    "holding_deposit_refunded_at",
                    DepositRefundedAt.Delivery,
                  );
                }
              }}
              className="col-span-3"
            />
            <DepositRefundedAtInput
              isEditable={
                values.holding_deposit_refunded_by !==
                HoldingDepositRefundedBy.Imoova
              }
              name="holding_deposit_refunded_at"
              label={tForm("refundedAt")}
              className="col-span-3"
            />
          </>
        ) : null}
      </div>
    </AdminSection>
  );
}

export function TripDataFields({
  relocation,
}: {
  relocation?: RelocationFieldsFragment;
}) {
  return (
    <>
      <TripDefaultsSync relocation={relocation} />
      <RelocationTripCard />
    </>
  );
}

export function TripDefaultsSync({
  relocation,
}: {
  relocation?: RelocationFieldsFragment;
}) {
  const { values } = useFormikContext<RelocationFormValues>();

  const departureOfficeId = values.departureOffice?.connect;
  const deliveryOfficeId = values.deliveryOfficeIds?.[0];

  if (!departureOfficeId || !deliveryOfficeId) {
    return null;
  }

  return (
    <TripDefaultsSyncForOffices
      relocation={relocation}
      departureOfficeId={departureOfficeId}
      deliveryOfficeId={deliveryOfficeId}
    />
  );
}

function TripDefaultsSyncForOffices({
  relocation,
  departureOfficeId,
  deliveryOfficeId,
}: {
  relocation?: RelocationFieldsFragment;
  departureOfficeId: string;
  deliveryOfficeId: string;
}) {
  const { setValues } = useFormikContext<RelocationFormValues>();

  const { data: supplierTripData } = useSuspenseGqlQuery(
    supplierTripByOfficesQuery,
    {
      officeAId: departureOfficeId,
      officeBId: deliveryOfficeId,
    },
  );

  useEffect(() => {
    if (!supplierTripData?.supplierTripByOffices || relocation) return;

    const trip = supplierTripData.supplierTripByOffices;

    setValues((old) => ({
      ...old,
      hire_units_allowed: trip.hire_units_allowed,
      extra_hire_units_allowed: trip.extra_hire_units_allowed,
      minimum_hire_units: Math.min(
        trip.minimum_hire_units,
        trip.hire_units_allowed,
      ),
      distance_allowed: trip.distance_allowed,
      is_ferry_required: trip.is_ferry_required,
    }));
  }, [supplierTripData?.supplierTripByOffices?.id, relocation?.id]);

  return null;
}

export function RouteAndVehicleFields({
  relocation,
}: {
  relocation?: RelocationFieldsFragment;
}) {
  const tForm = useTranslations("relocation.form");
  const { isEditing } = useFormEditState();
  const { values, setFieldValue } = useFormikContext<RelocationFormValues>();

  const { data: allOfficesData } = useSuspenseGqlQuery(
    supplierOfficeListQuery,
    {
      supplier_id: values.supplier.connect,
      page: 1,
      first: 200,
      archived: false,
      orderBy: [
        {
          column: QuerySupplierOfficesOrderByColumn.Name,
          order: SortOrder.Asc,
        },
      ],
    },
  );

  return (
    <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
      <SupplierOfficeInput
        name="departureOffice.connect"
        label={tForm("from")}
        className="col-span-3"
        getQueryVariables={(search) => ({
          first: 20,
          page: 1,
          search,
          supplier_id: values.supplier?.connect,
          archived: false,
        })}
        initialFixtureValues={{
          supplier: {
            connect: values.supplier.connect,
          },
        }}
        onChange={(office) => {
          if (office && values.deliveryOfficeIds?.length > 0) {
            setFieldValue(
              "deliveryOfficeIds",
              values.deliveryOfficeIds.filter((id) => id !== office.id),
            );
          }
        }}
      />

      <MultiSelectInput
        name="deliveryOfficeIds"
        label={tForm("to")}
        className="col-span-3"
        showSelectAll={false}
        LeadingIcon={MapPinIcon}
        options={allOfficesData.supplierOffices.data.map((office) => ({
          label: office.name,
          value: office.id,
        }))}
        value={values.deliveryOfficeIds ?? []}
        onChange={(selectedIds) => {
          setFieldValue("deliveryOfficeIds", selectedIds);
        }}
        getViewNode={(option) => (
          <Link
            className="text-blue-500"
            to="."
            search={(prev: Record<string, unknown>) => ({
              ...prev,
              officeId: option.value,
            })}
          >
            {option.label}
          </Link>
        )}
      />

      <VehicleInput
        name="vehicle.connect"
        label={tForm("vehicle")}
        className="col-span-2"
        getQueryVariables={(search) => ({
          first: 20,
          page: 1,
          search,
          supplier_id: values.supplier?.connect,
        })}
        initialFixtureValues={{
          supplier: {
            connect: values.supplier?.connect ?? null,
          },
        }}
        onChange={async (vehicle) => {
          if (!vehicle) {
            return;
          }

          await setFieldValue("hire_unit_type", vehicle.hire_unit_type);
          await setFieldValue("hire_unit_rate", vehicle.hire_unit_rate);
          await setFieldValue(
            "extra_hire_unit_rate",
            vehicle.extra_hire_unit_rate,
          );
          await setFieldValue(
            "extra_hire_unit_deposit_rate",
            vehicle.extra_hire_unit_deposit_rate,
          );
          await setFieldValue(
            "extra_hire_unit_supplier_net_rate",
            vehicle.extra_hire_unit_supplier_net_rate,
          );
        }}
      />

      <StepperInput
        min={Math.max(1, relocation?.booked_count ?? 0)}
        name={"quantity"}
        label={tForm("quantity")}
        tooltip={tForm("quantityTooltip")}
        viewNode={
          relocation ? (
            <QuantityBar
              booked={relocation.booked_count ?? 0}
              total={values.quantity ?? 0}
            />
          ) : (
            <span>{values.quantity}</span>
          )
        }
        description={
          relocation && isEditing
            ? tForm("quantityBookedHint", {
                booked: relocation.booked_count ?? 0,
                available: Math.max(
                  0,
                  (values.quantity ?? 0) - (relocation.booked_count ?? 0),
                ),
              })
            : undefined
        }
        className="col-span-1"
      />

      <RelocationReferencesInput
        name="lines"
        label={tForm("licensePlates")}
        placeholder={tForm("licensePlatesPlaceholder")}
        optionalLabel
        max={values.quantity || undefined}
        className="col-span-3"
      />
    </div>
  );
}

export function TripAllowanceFields() {
  return (
    <div className="grid grid-cols-3 gap-4 sm:grid-cols-12">
      <AvailabilityDateFields />
      <HireUnitFields />
      <DistanceFields />
    </div>
  );
}

export function AvailabilityDateFields() {
  const tForm = useTranslations("relocation.form");
  const { values, setFieldValue } = useFormikContext<RelocationFormValues>();

  const minimumHireUnits = values.minimum_hire_units ?? 0;
  const minimumCalendarDays =
    (values.hire_unit_type ?? HireUnitType.Day === HireUnitType.Day)
      ? minimumHireUnits - 1
      : minimumHireUnits;

  return (
    <>
      <div className="relative col-span-3">
        <DateInput
          name={"available_from_date"}
          label={tForm("availableFrom")}
          min={dayjs().local().format("YYYY-MM-DD")}
          max={dayjs().local().add(1, "year").format("YYYY-MM-DD")}
          onChange={(value) => {
            if (value === null) return;

            if (dayjs(value).isAfter(values.available_to_date)) {
              setFieldValue("available_to_date", null);
            }
          }}
        />
        {values.available_from_date &&
        dayjs(values.available_from_date).isBefore(dayjs(), "day") ? (
          <DateInPastWarning message={tForm("dateInPast")} />
        ) : null}
      </div>

      <div className="relative col-span-3">
        <DateInput
          name={"available_to_date"}
          label={tForm("availableTo")}
          min={dayjs(values.available_from_date)
            .add(minimumCalendarDays, "days")
            .local()
            .format("YYYY-MM-DD")}
          max={dayjs().local().add(1, "year").format("YYYY-MM-DD")}
        />
        {values.available_to_date &&
        dayjs(values.available_to_date).isBefore(dayjs(), "day") ? (
          <DateInPastWarning message={tForm("dateInPast")} />
        ) : null}
      </div>
    </>
  );
}

export function HireUnitFields({
  fieldClassName = "col-span-2",
}: {
  fieldClassName?: string;
}) {
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<RelocationFormValues>();
  const { unitLabel } = useHireUnitLabels();

  return (
    <>
      <HireUnitsInput
        unitLabel={unitLabel}
        currency={values.currency}
        hireUnitRate={values.hire_unit_rate}
        className={fieldClassName}
      />

      <NumberInput
        name="extra_hire_units_allowed"
        className={fieldClassName}
        inputProps={{
          min: 0,
        }}
        label={tForm("extraHire")}
        suffix={`${unitLabel}s`}
        tooltip={tForm("relocationHireTooltip", {
          unit: unitLabel,
          rate: formatCurrency(values.extra_hire_unit_rate, values.currency),
        })}
      />

      <NumberInput
        name="minimum_hire_units"
        className={fieldClassName}
        label={tForm("minimumHire")}
        tooltip={tForm("minimumHireTooltip", { unit: unitLabel })}
        suffix={`${unitLabel}s`}
        inputProps={{
          min: values.hire_unit_type === HireUnitType.Day ? 1 : 0,
          max: values.hire_units_allowed ?? 0,
        }}
      />

      <DepartureDatesWarning
        supplierId={values.supplier.connect ?? ""}
        vehicleId={values.vehicle?.connect ?? undefined}
        departureOfficeId={values.departureOffice?.connect ?? undefined}
        deliveryOfficeId={values.deliveryOfficeIds?.[0]}
        hireUnitType={values.hire_unit_type}
        availableFromDate={values.available_from_date}
        availableToDate={values.available_to_date}
        fixedLatestDepartureDate={values.fixed_latest_departure_date}
        hireUnitsAllowed={values.hire_units_allowed}
        extraHireUnitsAllowed={values.extra_hire_units_allowed}
        minimumHire={values.minimum_hire_units}
      />

      <Suspense fallback={null}>
        <HireUnitsWarning />
      </Suspense>
    </>
  );
}

export function DistanceFields() {
  const t = useTranslations("relocation");
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<RelocationFormValues>();
  const { unitLabel } = useHireUnitLabels();

  return (
    <>
      <DistanceAllowedInput
        name="distance_allowed"
        tooltip={tForm("distanceTooltip")}
        label={t(`distance_allowed_label.${values.measurement}`)}
        measurement={values.measurement}
        className="col-span-3"
      />

      {values.extra_hire_units_allowed ? (
        <DistanceAllowedInput
          label={`${t(`extra_distance_label.${values.measurement}`)} ${tForm("perExtraUnit", { unit: unitLabel })}`}
          name="extra_distance_allowed_per_extra_hire_unit"
          measurement={values.measurement}
          className="col-span-3"
          suffix={`/extra ${unitLabel}`}
          tooltip={tForm("extraDistanceTooltip")}
        />
      ) : null}

      <CurrencyInput
        currency={values.currency}
        name="excess_distance_rate"
        label={tForm("excessRateLabel", {
          label: t(
            `distance_allowed_label.${values.measurement}`,
          ).toLowerCase(),
        })}
        suffix={values.measurement === "IMPERIAL" ? "/ml" : "/km"}
        className="col-span-3"
        tooltip={tForm("excessRateTooltip")}
      />
    </>
  );
}

export function InsuranceField() {
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<RelocationFormValues>();

  return (
    <SupplierInsuranceOptionInput
      name="supplierInsuranceOption.connect"
      label={tForm("insurance")}
      className="col-span-3"
      getQueryVariables={(search) => ({
        search,
        page: 1,
        first: 20,
        supplierId: values.supplier?.connect,
      })}
      initialFixtureValues={{
        supplier: values.supplier,
      }}
    />
  );
}

export function AdditionalSettingsSections() {
  const { isAdmin } = useTenant();
  const { values, setFieldValue } = useFormikContext<RelocationFormValues>();
  const { capitalizedUnitLabel } = useHireUnitLabels();
  const variableRelocationRateEnabled = useFeatureFlag(
    FeatureFlagType.VariableRelocationRate,
  );

  return (
    <>
      <ConfigurationSection values={values} />
      <PricingSection
        values={values}
        setFieldValue={setFieldValue}
        currency={values.currency}
        isAdmin={isAdmin}
        variableRelocationRateEnabled={variableRelocationRateEnabled}
        capitalizedUnitLabel={capitalizedUnitLabel}
      />
      <FeesSection
        values={values}
        setFieldValue={setFieldValue}
        currency={values.currency}
      />
    </>
  );
}

function FieldsWithSupplier({
  initialShowAdvanced,
  relocation,
  showAdditionalSettings,
}: {
  initialShowAdvanced?: boolean;
  relocation?: RelocationFieldsFragment;
  showAdditionalSettings: boolean;
}) {
  const [showAdvanced, setShowAdvanced] = useState(initialShowAdvanced);
  const tForm = useTranslations("relocation.form");
  const { values } = useFormikContext<RelocationFormValues>();

  useSupplierFormDefaults();

  const routeReady = hasRouteAndVehicle(values);

  return (
    <div className="col-span-full space-y-8">
      <FormErrorSummary />

      <div className="space-y-6">
        <RouteAndVehicleFields relocation={relocation} />

        {routeReady ? (
          <>
            <Suspense fallback={<Skeleton className="h-[290px]" />}>
              <TripDataFields relocation={relocation} />
            </Suspense>

            <section>
              <TripAllowanceFields />
            </section>
          </>
        ) : null}
      </div>

      {routeReady ? (
        <>
          <div className="grid grid-cols-3 gap-4 sm:grid-cols-6">
            <InsuranceField />
          </div>

          <RelocationInclusionForm relocation={relocation} />

          {showAdditionalSettings ? (
            <>
              <div className="flex justify-center">
                <Button
                  plain
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <ChevronDownIcon
                    data-slot="icon"
                    className={clsx("transition-all duration-200", {
                      "rotate-180": showAdvanced,
                    })}
                  />
                  <span>{tForm("additionalSettings")}</span>
                </Button>
              </div>

              {showAdvanced ? <AdditionalSettingsSections /> : null}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
